import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FundClassification } from '@/models/common/FundClassification';
import Checkbox from '@/components/ui/Checkbox/Checkbox';
import { PRIVACY_POLICY_URL } from '@/data/privacyPolicy';
import fundSettingsIcon from '@/assets/icons/fundSettings.svg';
import userIcon from '@/assets/icons/user.svg';
import documentsIcon from '@/assets/icons/documents.svg';
import styles from './IntroScreen.module.scss';

interface IntroScreenProps {
  classification: FundClassification.Welfare | FundClassification.Community;
  /** Receives the moment the applicant accepted the privacy policy, which is stored with the request. */
  onStart: (privacyAcceptedAt: string) => void;
}

interface IntroStepItem {
  title: string;
  description: string;
}

const stepIcons = [fundSettingsIcon, userIcon, documentsIcon];

const IntroScreen = ({ classification, onStart }: IntroScreenProps) => {
  const { t } = useTranslation();

  // The applicant fills in personal details of coordinators and supporteds, so the consent to the
  // privacy policy is asked for here - before any of it is typed - and the process cannot start
  // without it.
  const [privacyAcceptedAt, setPrivacyAcceptedAt] = useState<string | null>(null);
  const [showPrivacyError, setShowPrivacyError] = useState(false);
  const privacyCheckboxRef = useRef<HTMLInputElement>(null);

  const steps = t(`openFundRequest.intro.steps.${classification}`, {
    returnObjects: true,
  }) as IntroStepItem[];

  const handleStart = () => {
    if (!privacyAcceptedAt) {
      setShowPrivacyError(true);
      privacyCheckboxRef.current?.focus();
      return;
    }
    onStart(privacyAcceptedAt);
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.stepsList}>
        {steps.map((step, index) => (
          <div className={styles.stepRow} key={step.title}>
            <div className={styles.iconBox}>
              <img src={stepIcons[index] ?? stepIcons[stepIcons.length - 1]} alt="" aria-hidden="true" />
            </div>
            <div className={styles.stepTextWrapper}>
              <div className={styles.stepTitleRow}>
                <span className={`body-lr-semibold ${styles.stepNumber}`}>{index + 1}.</span>
                <span className={`body-lr-semibold ${styles.stepTitle}`}>{step.title}</span>
              </div>
              <span className={`body-mr-regular ${styles.stepDescription}`}>{step.description}</span>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.consentBlock}>
        {/*
          The consent text carries a link to the policy, so it cannot sit inside a <label> - a click
          on the link would toggle the checkbox as well. The checkbox is named by the text instead.
        */}
        <div className={`${styles.consentRow} ${showPrivacyError ? styles.consentRowError : ''}`}>
          <Checkbox
            ref={privacyCheckboxRef}
            checked={privacyAcceptedAt !== null}
            onCheckedChange={(value) => {
              setPrivacyAcceptedAt(value ? new Date().toISOString() : null);
              if (value) setShowPrivacyError(false);
            }}
            ariaLabelledBy="open-fund-privacy-consent-text"
            ariaDescribedBy={showPrivacyError ? 'open-fund-privacy-consent-error' : undefined}
            ariaInvalid={showPrivacyError}
            ariaRequired
          />
          <span id="open-fund-privacy-consent-text" className={styles.consentText}>
            {t('openFundRequest.intro.privacyConsent.before')}
            <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" className={styles.consentLink}>
              {t('openFundRequest.intro.privacyConsent.linkText')}
            </a>
            {t('openFundRequest.intro.privacyConsent.after')}
          </span>
        </div>
        {showPrivacyError && (
          <span id="open-fund-privacy-consent-error" role="alert" className={`body-sm-regular ${styles.consentError}`}>
            {t('openFundRequest.intro.privacyConsent.error')}
          </span>
        )}
      </div>

      <button className={styles.startButton} onClick={handleStart} type="button">
        <span className="body-lr-semibold">{t('openFundRequest.intro.start')}</span>
        <span className={styles.startChevron} aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>
    </div>
  );
};

export default IntroScreen;
